import React from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Box,
  LinearProgress,
  Backdrop,
} from "@mui/material";
import MDEditor from "@uiw/react-md-editor";
import { nanoid } from "nanoid";
import StandardButton from "../../../../components/StandardButton";
import {
  useUpdateExerciseBigQuery,
  useCreateCustomExerciseBigQuery,
} from "../../../../hooks/useExercisesBigQuery";
import {
  useMuscleGroupsBigQuery,
  useEquipmentBigQuery,
} from "../../../../hooks/useStaticDataBigQuery";
import NewColors from "../../../../themes/NewColors";

const ExerciseDialog = (props) => {
  const [open, setOpen] = React.useState(false);

  const [exerciseId, setExerciseId] = React.useState(props.id);
  const [idEdit, setIdEdit] = React.useState(true);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [instructions, setInstructions] = React.useState("");
  const [difficulty, setDifficulty] = React.useState("beginner");
  const [icon, setIcon] = React.useState("");
  const [mainMuscleGroup, setMainMuscleGroup] = React.useState("");
  const [isCustom, setIsCustom] = React.useState(false);
  const [secondaryMuscleGroup, setSeceondaryMuscleGroup] = React.useState("");
  const [exerciseEquipment, setExerciseEquipment] = React.useState("");

  // Validation state
  const [nameError, setNameError] = React.useState("");
  const [descriptionError, setDescriptionError] = React.useState("");

  const [muscleGroups, setMuscleGroups] = React.useState([]);
  const [equipment, setEquipment] = React.useState([]);

  // BigQuery hooks for static data
  const { data: muscleGroupsData } = useMuscleGroupsBigQuery();
  const { data: equipmentData } = useEquipmentBigQuery();

  // BigQuery mutations
  const updateExerciseMutation = useUpdateExerciseBigQuery();
  const createExerciseMutation = useCreateCustomExerciseBigQuery();

  const isLoading =
    updateExerciseMutation.isPending || createExerciseMutation.isPending;

  // Helper function to validate if a value exists in the available options
  const validateMuscleGroupValue = (value, muscleGroupsList) => {
    if (!value || !muscleGroupsList || muscleGroupsList.length === 0) {
      return "";
    }
    const exists = muscleGroupsList.some((mg) => {
      // Handle both Firebase (with .data() function) and BigQuery (plain object) formats
      const data = typeof mg.data === "function" ? mg.data() : mg;
      return data && data.id === value;
    });
    return exists ? value : "";
  };

  const validateEquipmentValue = (value, equipmentList) => {
    if (!value || !equipmentList || equipmentList.length === 0) {
      return "";
    }
    // Check if it's one of the standard values
    if (value === "all" || value === null || value === "") {
      return value;
    }
    const exists = equipmentList.some((eq) => {
      // Handle both Firebase (with .data() function) and BigQuery (plain object) formats
      const data = typeof eq.data === "function" ? eq.data() : eq;
      return data && data.id === value;
    });
    return exists ? value : "";
  };

  // Handle dialog open/close and initial state setup
  React.useEffect(() => {
    setOpen(props.open);

    if (!props.exercise) {
      // Creating new exercise
      setExerciseId(nanoid());
      setIdEdit(false);
      setName("");
      setDescription("");
      setInstructions("");
      setDifficulty("beginner");
      setIcon("");
      setMainMuscleGroup("");
      setSeceondaryMuscleGroup("");
      setExerciseEquipment("");
      setIsCustom(false);
      setNameError("");
      setDescriptionError("");
    } else {
      // Editing existing exercise - set values directly without validation
      setExerciseId(props.exercise.id);
      setIdEdit(false);
      setName(props.exercise.name || "");
      setDescription(props.exercise.description || "");
      setInstructions(props.exercise.instructions || "");
      setDifficulty(props.exercise.difficulty || "beginner");
      setIcon(props.exercise.icon || "");

      const mainMGValue =
        props.exercise.mainMuscleGroups &&
        props.exercise.mainMuscleGroups.length > 0
          ? props.exercise.mainMuscleGroups[0]
          : "";
      setMainMuscleGroup(mainMGValue);

      const secondaryMGValue =
        props.exercise.secondaryMuscleGroups &&
        props.exercise.secondaryMuscleGroups.length > 0
          ? props.exercise.secondaryMuscleGroups[0]
          : "";
      setSeceondaryMuscleGroup(secondaryMGValue);

      const equipmentValue =
        props.exercise.equipment && props.exercise.equipment.length > 0
          ? props.exercise.equipment[0]
          : "";
      setExerciseEquipment(equipmentValue);

      setIsCustom(props.exercise.isCustom || false);
      setNameError("");
      setDescriptionError("");
    }
  }, [props.open, props.exercise?.id]); // Only depend on open state and exercise ID

  // Handle BigQuery static data updates - direct assignment without validation
  React.useEffect(() => {
    if (muscleGroupsData) {
      setMuscleGroups(muscleGroupsData);
    }
  }, [muscleGroupsData]);

  React.useEffect(() => {
    if (equipmentData) {
      setEquipment(equipmentData);
    }
  }, [equipmentData]);

  const isEdit = React.useMemo(() => {
    if (props.exercise) {
      return true;
    }
    return false;
  }, [props.exercise]);

  // Validation function
  const validateForm = () => {
    let isValid = true;

    // Reset errors
    setNameError("");
    setDescriptionError("");

    // Validate name
    if (!name || name.trim() === "") {
      setNameError("Exercise name is required");
      isValid = false;
    }

    // Validate description
    if (!description || description.trim() === "") {
      setDescriptionError("Exercise description is required");
      isValid = false;
    }

    return isValid;
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      return;
    }

    // Validate and clean up the data before saving
    const validatedIcon = validateMuscleGroupValue(icon, muscleGroups);
    const validatedMainMG = validateMuscleGroupValue(
      mainMuscleGroup,
      muscleGroups,
    );
    const validatedSecondaryMG = validateMuscleGroupValue(
      secondaryMuscleGroup,
      muscleGroups,
    );
    const validatedEquipment = validateEquipmentValue(
      exerciseEquipment,
      equipment,
    );

    const exerciseData = {
      id: exerciseId,
      name: name,
      description: description,
      instructions: instructions,
      difficulty: difficulty,
      icon: validatedIcon,
      isCustom: isCustom,
      mainMuscleGroups: validatedMainMG ? [validatedMainMG] : [],
      secondaryMuscleGroups: validatedSecondaryMG ? [validatedSecondaryMG] : [],
      equipment:
        validatedEquipment && validatedEquipment !== ""
          ? [validatedEquipment]
          : [],
      // Preserve videos and images arrays from original exercise when editing
      ...(isEdit &&
        props.exercise && {
          videos: props.exercise.videos || [],
          images: props.exercise.images || [],
        }),
    };

    try {
      if (isEdit) {
        await updateExerciseMutation.mutateAsync({
          exerciseId: exerciseId,
          exerciseData: exerciseData,
        });
      } else {
        await createExerciseMutation.mutateAsync({
          exerciseId: exerciseId,
          exerciseData: exerciseData,
        });
      }

      // Close dialog and notify parent on success
      if (props.onDialogClosed) {
        props.onDialogClosed(exerciseData);
      }
      setOpen(false);
    } catch (error) {
      console.error("Error saving exercise:", error);
      // Keep dialog open on error so user can retry
    }
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={
        isLoading
          ? undefined
          : () => {
              if (props.onDialogClosed) {
                props.onDialogClosed(null);
                setOpen(false);
              }
            }
      }
    >
      {/* Loading backdrop to prevent interaction during save */}
      {isLoading && (
        <Backdrop
          sx={{
            color: NewColors.white,
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={isLoading}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress color="inherit" />
            <Typography sx={{ mt: 2 }}>
              {isEdit ? "Updating exercise..." : "Creating exercise..."}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <DialogTitle>{isEdit ? `Edit Exercise` : `Create Exercise`}</DialogTitle>
      <DialogContent>
        {/* Progress indicator during exercise creation/update */}
        {isLoading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, textAlign: "center" }}>
              {isEdit
                ? "Updating exercise... Please wait."
                : "Creating exercise... Please wait."}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        <Stack spacing={1}>
          <Stack>
            <Typography>Unique ID:</Typography>
            <TextField
              disabled={!idEdit || isLoading}
              variant="outlined"
              style={{ width: "100%" }}
              value={exerciseId}
              onChange={(event) => {
                setExerciseId(event.target.value);
              }}
            />
          </Stack>
          <Stack>
            <Typography style={{ width: 200 }}>Exercise name *</Typography>
            <TextField
              disabled={isLoading}
              variant="outlined"
              style={{ width: "100%" }}
              value={name}
              error={!!nameError}
              helperText={nameError}
              onChange={(event) => {
                setName(event.target.value);
                // Clear error when user starts typing
                if (nameError) {
                  setNameError("");
                }
              }}
            />
          </Stack>
          <Stack>
            <Typography style={{ width: 200 }}>Add Description *</Typography>
            <div data-color-mode="light">
              <MDEditor
                preview="edit"
                height={150}
                style={{
                  width: "100%",
                  minWidth: 800,
                  border: descriptionError
                    ? `1px solid ${NewColors.status.error}`
                    : undefined,
                  borderRadius: descriptionError ? "4px" : undefined,
                }}
                placeholder="Enter healthy recipe description or video link here"
                value={description}
                onChange={
                  !isLoading
                    ? (value) => {
                        setDescription(value);
                        // Clear error when user starts typing
                        if (descriptionError) {
                          setDescriptionError("");
                        }
                      }
                    : undefined
                }
                data-disabled={isLoading}
              />
            </div>
            {descriptionError && (
              <Typography
                variant="caption"
                sx={{
                  color: "error.main",
                  mt: 0.5,
                  ml: 1.75,
                  fontSize: "0.75rem",
                }}
              >
                {descriptionError}
              </Typography>
            )}
          </Stack>
          <Stack>
            <Typography style={{ width: 200 }}>Add Instructions</Typography>
            <div data-color-mode="light">
              <MDEditor
                preview="edit"
                height={150}
                style={{ width: "100%", minWidth: 800 }}
                placeholder="Enter healthy recipe description or video link here"
                value={instructions}
                onChange={
                  !isLoading
                    ? (value) => {
                        setInstructions(value);
                      }
                    : undefined
                }
                data-disabled={isLoading}
              />
            </div>
          </Stack>
          <Stack>
            <Typography style={{ width: 200 }}>Difficulty</Typography>
            <Select
              disabled={isLoading}
              size="small"
              defaultValue={difficulty}
              value={difficulty}
              style={{ width: "100%" }}
              variant="outlined"
              onChange={(event) => {
                setDifficulty(event.target.value);
              }}
            >
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
              <MenuItem value="expert">Expert</MenuItem>
            </Select>
          </Stack>
          <Stack>
            <Typography style={{ width: 200 }}>Icon</Typography>
            <Select
              disabled={isLoading}
              size="small"
              value={muscleGroups.length > 0 ? icon : ""}
              style={{ width: "100%" }}
              variant="outlined"
              onChange={(event) => {
                setIcon(event.target.value);
              }}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Icon</em>
              </MenuItem>
              {muscleGroups.map((mg) => {
                // Handle both Firebase (with .data() function) and BigQuery (plain object) formats
                const data = typeof mg.data === "function" ? mg.data() : mg;
                return (
                  <MenuItem key={data.id} value={data.id}>
                    {data.name}
                  </MenuItem>
                );
              })}
            </Select>
          </Stack>
          <Stack>
            <Typography style={{ width: 200 }}>Main Muscle Group</Typography>
            <Select
              disabled={isLoading}
              size="small"
              value={muscleGroups.length > 0 ? mainMuscleGroup : ""}
              style={{ width: "100%" }}
              variant="outlined"
              onChange={(event) => {
                setMainMuscleGroup(event.target.value);
              }}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Main Muscle Group</em>
              </MenuItem>
              {muscleGroups.map((mg) => {
                // Handle both Firebase (with .data() function) and BigQuery (plain object) formats
                const data = typeof mg.data === "function" ? mg.data() : mg;
                return (
                  <MenuItem key={data.id} value={data.id}>
                    {data.name}
                  </MenuItem>
                );
              })}
            </Select>
          </Stack>
          <Stack>
            <Typography style={{ width: 200 }}>
              Secondary Muscle Group
            </Typography>
            <Select
              disabled={isLoading}
              size="small"
              value={muscleGroups.length > 0 ? secondaryMuscleGroup : ""}
              style={{ width: "100%" }}
              variant="outlined"
              onChange={(event) => {
                setSeceondaryMuscleGroup(event.target.value);
              }}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Secondary Muscle Group</em>
              </MenuItem>
              {muscleGroups.map((mg) => {
                // Handle both Firebase (with .data() function) and BigQuery (plain object) formats
                const data = typeof mg.data === "function" ? mg.data() : mg;
                return (
                  <MenuItem key={data.id} value={data.id}>
                    {data.name}
                  </MenuItem>
                );
              })}
            </Select>
          </Stack>
          <Stack>
            <Typography>Equipment</Typography>
            <Select
              disabled={isLoading}
              size="small"
              value={equipment.length > 0 ? exerciseEquipment : ""}
              style={{ width: "100%" }}
              variant="outlined"
              onChange={(event) => {
                setExerciseEquipment(event.target.value);
              }}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Equipment</em>
              </MenuItem>
              <MenuItem value="all">All</MenuItem>
              {equipment.map((eq) => {
                // Handle both Firebase (with .data() function) and BigQuery (plain object) formats
                const data = typeof eq.data === "function" ? eq.data() : eq;
                return (
                  <MenuItem key={data.id} value={data.id}>
                    {data.name}
                  </MenuItem>
                );
              })}
            </Select>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (props.onDialogClosed) {
              props.onDialogClosed(null);
            }
            setOpen(false);
          }}
          color="primary"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <StandardButton
          variant="primary"
          onClick={handleSave}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading
            ? isEdit
              ? "Updating Exercise..."
              : "Creating Exercise..."
            : isEdit
              ? "Update Exercise"
              : "Create Exercise"}
        </StandardButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExerciseDialog;
